import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { prettify } from '../../helpers/prices';
import { getItemPrice, getPrivateLeasingPromo } from '../../selectors/item';

import { asHtml } from '../../helpers/markdown';
import { Box } from '../../styled-components/components/Box';
import Checklist from '../../styled-components/components/Checklist';
import Content from '../../styled-components/components/Content';
import { LayoutGrid, LayoutGridItem } from '../../styled-components/components/LayoutGrid';
import { Link } from '../../styled-components/components/Link';
import { LinkButton } from '../../styled-components/components/LinkButton';
import Modal from '../../styled-components/components/Modal';
import { StackLarge, StackSmall, StackTiny } from '../../styled-components/components/Stack';
import { Text } from '../../styled-components/components/Text';
import { ImgFullWidth } from '../../styled-components/components/Utilities';
import PriceRow from './price-row';

const PrivateLeasing = () => {
  const { t } = useTranslation(['common', 'item']);
  const data = useSelector(getItemPrice);
  const promo = useSelector(getPrivateLeasingPromo);

  const [modal, setModal] = useState(false);

  const leasingPrice = data?.leasingPrice;
  const leasingDeposit = data?.leasingDeposit;

  const onOpenModal = useCallback(() => setModal(true), []);
  const onCloseModal = useCallback(() => setModal(false), []);

  const checkListItems = [];

  if (promo?.insuranceIncluded) {
    checkListItems.push(t('privateLeasing.insuranceIncluded', { ns: 'item' }));
  }

  if (promo?.serviceIncluded) {
    checkListItems.push(t('privateLeasing.serviceIncluded', { ns: 'item' }));
  }

  if (promo?.winterTiresIncluded) {
    checkListItems.push(t('privateLeasing.winterTiresIncluded', { ns: 'item' }));
  }

  if (!leasingPrice) return null;

  return (
    <StackSmall>
      {modal && promo && (
        <Modal onClose={onCloseModal} heading={t('privateLeasing.modalTitle', { ns: 'item' })}>
          {promo.image && (
            <StackLarge>
              <ImgFullWidth src={promo.image} alt={promo.title} />
            </StackLarge>
          )}
          <StackLarge>
            <StackSmall>
              <Text $weight="medium">{promo.title}</Text>
            </StackSmall>
            {promo.description && (
              <StackSmall>
                <Content
                  fullWidth
                  dangerouslySetInnerHTML={{ __html: asHtml(promo.description) }}
                />
              </StackSmall>
            )}
            {promo.link && (
              <StackSmall>
                <Link
                  href={promo.link.url}
                  title={promo.link.name}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  data-wayke-action="private-lease-external-link"
                >
                  {promo.link.name}
                </Link>
              </StackSmall>
            )}
          </StackLarge>
          {checkListItems.length > 0 && (
            <StackLarge>
              <Checklist items={checkListItems} />
            </StackLarge>
          )}
          <StackLarge>
            <LayoutGrid $spacing={3}>
              <LayoutGridItem $widthSm={50} $widthMd={33.333}>
                <StackTiny>
                  <Text $weight="medium" $size="small">
                    {t('privateLeasing.monthlyCost', { ns: 'item' })}
                  </Text>
                </StackTiny>
                <StackTiny>{t('Price per month', { ns: 'common', count: leasingPrice })}</StackTiny>
              </LayoutGridItem>
              <LayoutGridItem $widthSm={50} $widthMd={33.333}>
                <StackTiny>
                  <Text $weight="medium" $size="small">
                    {t('privateLeasing.binding', { ns: 'item' })}
                  </Text>
                </StackTiny>
                <StackTiny>{prettify(promo.periodMonths, 'månader')}</StackTiny>
              </LayoutGridItem>
              <LayoutGridItem $widthSm={50} $widthMd={33.333}>
                <StackTiny>
                  <Text $weight="medium" $size="small">
                    {t('privateLeasing.annualMileage', { ns: 'item' })}
                  </Text>
                </StackTiny>
                <StackTiny>
                  {t('Miles per year', { ns: 'common', count: promo.mileagePerYear })}
                </StackTiny>
              </LayoutGridItem>
            </LayoutGrid>
          </StackLarge>
        </Modal>
      )}
      <Box $relative>
        <StackSmall>
          <div data-wx-pricelist="">
            <PriceRow
              title={t('privateLeasing.modalTitle', { ns: 'item' })}
              value={t('Price per month', { ns: 'common', count: leasingPrice })}
              shouldIndicateReducedPrice={false}
              isReducedPrice={false}
            />
            {!!leasingDeposit && (
              <PriceRow
                title={t('privateLeasing.deposit', { ns: 'item' })}
                value={prettify(leasingDeposit, 'kr')}
                shouldIndicateReducedPrice={false}
                isReducedPrice={false}
              />
            )}
          </div>
        </StackSmall>
        {promo && (
          <StackSmall>
            <Text $tone="alt" $size="xsmallStatic">
              {t('privateLeasing.includingMileageInPeriod', {
                ns: 'item',
                mileage: promo.mileagePerYear,
                length: promo.periodMonths,
              })}
              <LinkButton
                onClick={onOpenModal}
                title={t('privateLeasing.readMore.title', { ns: 'item' })}
                data-wayke-action="private-lease-read-more"
                $hoverGhost
                $block
              >
                {t('privateLeasing.readMore.text', { ns: 'item' })}
              </LinkButton>
            </Text>
          </StackSmall>
        )}
      </Box>
    </StackSmall>
  );
};

export default PrivateLeasing;
